import { useCallback, useEffect, useState } from 'react';
import { api } from '../../../../services/api';
import type { LancamentoCredito, PacoteCredito, SaldoCredito } from './types';

const LIMITE_KM_AVISO_PADRAO = 200;

/**
 * `avisarSaldoBaixoKm` buscado por conta própria, na entrada da própria aba —
 * DD-09/FR-011: no app, esse limiar só é populado pelo módulo do wizard
 * (research.md — achado crítico), o que faria "Créditos" mostrar sempre o
 * default (200) se o cooperado nunca abriu "Emitir" antes. O painel não tem
 * essa ordem obrigatória de telas, então busca aqui, sem depender de nada.
 */
export function useLimiteKmAviso() {
  const [limite, setLimite] = useState(LIMITE_KM_AVISO_PADRAO);

  useEffect(() => {
    api.get('/atf/parametros')
      .then(res => {
        const v = Number(res.data?.avisarSaldoBaixoKm);
        if (Number.isFinite(v) && v > 0) setLimite(v);
      })
      .catch(() => { /* fica no default — mesma tolerância do app */ });
  }, []);

  return limite;
}

export function useSaldo() {
  const [saldo, setSaldo] = useState<SaldoCredito | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const recarregar = useCallback(() => {
    setErro(null);
    setCarregando(true);
    api.get<SaldoCredito>('/cooperado/creditos/saldo')
      .then(res => setSaldo(res.data))
      .catch(() => setErro('Não foi possível carregar seu saldo'))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(recarregar, [recarregar]);

  return { saldo, carregando, erro, recarregar };
}

export function useExtrato() {
  const [lancamentos, setLancamentos] = useState<LancamentoCredito[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    api.get<LancamentoCredito[]>('/cooperado/creditos/extrato')
      .then(res => setLancamentos(res.data))
      .catch(() => setErro('Não foi possível carregar seu extrato'))
      .finally(() => setCarregando(false));
  }, []);

  return { lancamentos, carregando, erro };
}

export function usePacotes() {
  const [pacotes, setPacotes] = useState<PacoteCredito[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    api.get<PacoteCredito[]>('/cooperado/creditos/pacotes')
      .then(res => setPacotes(res.data))
      .catch(() => setErro('Não foi possível carregar os pacotes'))
      .finally(() => setCarregando(false));
  }, []);

  return { pacotes, carregando, erro };
}
